/* ══ v8.9: data-source indicator (landing page) ═════════════════════════════
   Always-visible provenance beneath "As of": WHERE the data on screen came from, and what Save
   will do. v18: the source is the FlightDeck server this page was loaded from, and the person
   whose name every save is published under. */
let _dataSrcStale = false;        /* kept for the overview's stale banner slot; never set in v18 */
const LIST_ORDER_KEY_PREFIX = 'flightdeck.listOrder.v1:';
const _listOrderCollator = new Intl.Collator('en-GB', { numeric: true, sensitivity: 'base' });
let _listOrderPrefs = { programmes: { mode: 'default', ids: [] }, projects: Object.create(null) };
let _listOrderBoundFolderKey = null;
let _listOrderSessionChanged = false;
let _listOrderStorageOk = true;
/* v17.5 P4 follow-up: arrow visibility is a transient editing state, independent of the
   persisted order preference. P5 reuses this owner-aware controller for workstreams. */
let _reorderUi = null;   /* {kind:'programme'|'project'|'workstream',ownerId,folderKey,editing} */

function _listOrderFresh() { return { programmes: { mode: 'default', ids: [] }, projects: Object.create(null) }; }
function _listOrderFolderKey() {
  /* v18: "whose order is this" is the signed-in user, not the folder the launcher served. */
  return _srvUser && _srvUser.username ? 'user:' + _srvUser.username : null;
}
function _listOrderEntry(raw) {
  const modes = new Set(['default', 'nameAsc', 'nameDesc', 'manual']);
  const mode = raw && modes.has(raw.mode) ? raw.mode : 'default';
  const seen = new Set(), ids = [];
  if (raw && Array.isArray(raw.ids)) raw.ids.forEach(function (id) {
    if (typeof id !== 'string' || !id.trim() || seen.has(id)) return;
    seen.add(id); ids.push(id);
  });
  return { mode: mode, ids: ids };
}
function _listOrderSanitise(raw) {
  const out = _listOrderFresh();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  out.programmes = _listOrderEntry(raw.programmes);
  if (raw.projects && typeof raw.projects === 'object' && !Array.isArray(raw.projects)) {
    Object.keys(raw.projects).forEach(function (pid) {
      if (pid) out.projects[pid] = _listOrderEntry(raw.projects[pid]);
    });
  }
  return out;
}
function _listOrderEnsureBinding() {
  const folderKey = _listOrderFolderKey();
  if (!folderKey || folderKey === _listOrderBoundFolderKey) return;
  if (_listOrderBoundFolderKey) _listOrderFailureNotified = false;
  /* A confirmed folder replacement keeps the visible owner but always ends its transient
     reorder session. Initial folder discovery belongs to the current load and may keep it. */
  if (_reorderUi) {
    if (!_reorderUi.folderKey) _reorderUi.folderKey = folderKey;
    else if (_reorderUi.folderKey !== folderKey) {
      _reorderUi.folderKey = folderKey;
      _reorderUi.editing = false;
    }
  }
  const storageKey = LIST_ORDER_KEY_PREFIX + folderKey;
  if (_listOrderSessionChanged) {
    _listOrderBoundFolderKey = folderKey;
    _listOrderPersist();
    return;
  }
  const raw = _srvPref('global', storageKey);
  _listOrderPrefs = (raw === undefined) ? _listOrderFresh() : _listOrderSanitise(raw);
  _listOrderStorageOk = true;
  _listOrderBoundFolderKey = folderKey;
}
function _listOrderPersist() {
  _listOrderSessionChanged = true;
  const folderKey = _listOrderFolderKey();
  if (!folderKey) return false;
  _listOrderBoundFolderKey = folderKey;
  _srvSetPref('global', LIST_ORDER_KEY_PREFIX + folderKey, JSON.parse(JSON.stringify(_listOrderPrefs)));
  _listOrderStorageOk = true;
  _listOrderFailureNotified = false;
  return _listOrderStorageOk;
}
/* One neutral notice per continuous failure episode, shared by both personal lists. */
let _listOrderFailureNotified = false;
function _listOrderNotifyFailure(saved) {
  if (saved || _listOrderFailureNotified) return;
  _listOrderFailureNotified = true;
  showToast('Your list order couldn’t be saved and may reset when you reload.', { duration: 3500 });
}
function _listOrderEntryFor(kind, programmeId, create) {
  _listOrderEnsureBinding();
  if (kind === 'programme') return _listOrderPrefs.programmes;
  const key = String(programmeId || '');
  if (!_listOrderPrefs.projects[key] && create) {
    _listOrderPrefs.projects[key] = { mode: 'default', ids: [] };
  }
  return _listOrderPrefs.projects[key] || { mode: 'default', ids: [] };
}
function _listOrderId(kind, item) {
  return String(kind === 'programme' ? (item && item.id || '') : (item && item.uid || ''));
}
function _listOrderName(kind, item) {
  const v = item && typeof item.name === 'string' ? item.name.trim() : '';
  return v || (kind === 'programme' ? 'Unnamed programme' : 'Untitled project');
}
function _listOrderStable(a, b, kind, descending) {
  const byName = _listOrderCollator.compare(_listOrderName(kind, a), _listOrderName(kind, b));
  if (byName) return (descending ? -1 : 1) * byName;
  return _listOrderByKey(a, b, kind);
}
function _listOrderByKey(a, b, kind) {
  const ak = _listOrderId(kind, a), bk = _listOrderId(kind, b);
  const aa = Array.from(ak), bb = Array.from(bk), n = Math.min(aa.length, bb.length);
  for (let i = 0; i < n; i++) {
    const ac = aa[i].codePointAt(0), bc = bb[i].codePointAt(0);
    if (ac !== bc) return ac < bc ? -1 : 1;
  }
  return aa.length < bb.length ? -1 : aa.length > bb.length ? 1 : 0;
}
function _listOrderOrdered(items, kind, programmeId) {
  const copy = (Array.isArray(items) ? items : []).filter(Boolean).slice();
  const pref = _listOrderEntryFor(kind, programmeId, false);
  if (pref.mode === 'nameAsc' || pref.mode === 'nameDesc') {
    const descending = pref.mode === 'nameDesc';
    return copy.sort(function (a, b) { return _listOrderStable(a, b, kind, descending); });
  }
  if (pref.mode !== 'manual') return copy;
  const rank = new Map(pref.ids.map(function (id, i) { return [id, i]; }));
  return copy.sort(function (a, b) {
    const ai = rank.has(_listOrderId(kind, a)) ? rank.get(_listOrderId(kind, a)) : Infinity;
    const bi = rank.has(_listOrderId(kind, b)) ? rank.get(_listOrderId(kind, b)) : Infinity;
    return ai === bi ? _listOrderByKey(a, b, kind) : ai - bi;
  });
}
function _listOrderAll(kind, programmeId) {
  const db = window.__DB__ || {};
  return kind === 'programme'
    ? (Array.isArray(db.programmes) ? db.programmes.filter(function (p) { return p && !p.del; }) : [])
    : (Array.isArray(db.projects) ? db.projects.filter(function (p) {
        return p && !p.del && String(p.programmeId) === String(programmeId || '');
      }) : []);
}
function _reorderUiOwner(kind, programmeId) {
  if (kind === 'programme') return 'portfolio';
  return String(programmeId || '');
}
function _reorderUiBind(kind, programmeId) {
  const ownerId = _reorderUiOwner(kind, programmeId);
  const folderKey = _listOrderFolderKey();
  if (_reorderUi && _reorderUi.kind === kind && _reorderUi.ownerId === ownerId) {
    /* Initial folder discovery belongs to this load. A different confirmed folder does not. */
    if (!_reorderUi.folderKey && folderKey) _reorderUi.folderKey = folderKey;
    else if (_reorderUi.folderKey && folderKey && _reorderUi.folderKey !== folderKey) {
      _reorderUi = { kind, ownerId, folderKey, editing: false };
    }
    return _reorderUi;
  }
  _reorderUi = { kind, ownerId, folderKey, editing: false };
  return _reorderUi;
}
function _reorderUiReset() { _reorderUi = null; }
function _reorderUiMatches(kind, programmeId) {
  if (!_reorderUi || _reorderUi.kind !== kind
      || _reorderUi.ownerId !== _reorderUiOwner(kind, programmeId)) return false;
  const folderKey = _listOrderFolderKey();
  return !(_reorderUi.folderKey && folderKey && _reorderUi.folderKey !== folderKey);
}
function _reorderUiEditing(kind, programmeId) {
  return _reorderUiMatches(kind, programmeId) && !!_reorderUi.editing;
}
function _listOrderHasMovablePair(kind, programmeId) {
  if (_listOrderSearchActive()) return false;
  const items = _listOrderAll(kind, programmeId);
  const live = items.filter(function (x) { return x && !x.archived; }).length;
  const showArchived = kind === 'programme' ? !!_pfShowArchived : !!_ovShowArchived;
  const archived = showArchived
    ? items.filter(function (x) { return x && !!x.archived; }).length : 0;
  return live > 1 || archived > 1;
}
function _listOrderIsInteractionTarget(destination) {
  return !!(destination && destination.classList
    && (destination.classList.contains('list-order-select')
      || destination.classList.contains('list-order-toggle')
      || destination.classList.contains('list-order-move')));
}
function _listOrderDeferEditorRepaint(destination, repaint) {
  /* The focus transition happens before click. Let the intended button activation finish,
     then repaint the committed edit only if that activation did not already replace the row. */
  if (!destination || typeof repaint !== 'function') return;
  const kind = destination.dataset.orderKind || '';
  const programmeId = destination.dataset.programmeId || '';
  const orderId = destination.dataset.orderId || '';
  const orderDir = destination.dataset.orderDir || '';
  const initialText = destination.textContent;
  let armed = true;
  const cleanup = function () {
    destination.removeEventListener('click', finish);
    destination.removeEventListener('blur', finish);
  };
  const finish = function () {
    if (!armed) return;
    armed = false;
    cleanup();
    setTimeout(function () {
      if (!destination.isConnected) return; /* a move already performed the required repaint */
      const restoreFocus = document.activeElement === destination;
      repaint();
      if (!restoreFocus) return;
      let selector = `.list-order-toggle[data-order-kind="${CSS.escape(kind)}"][data-programme-id="${CSS.escape(programmeId)}"]`;
      if (orderId) selector = `.list-order-move[data-order-kind="${CSS.escape(kind)}"][data-programme-id="${CSS.escape(programmeId)}"]`
        + `[data-order-id="${CSS.escape(orderId)}"][data-order-dir="${CSS.escape(orderDir)}"]`;
      const next = document.querySelector(selector);
      if (next) next.focus();
    }, 0);
  };
  destination.addEventListener('click', finish, { once: true });
  destination.addEventListener('blur', finish, { once: true });
  /* Browsers may snapshot a click's listeners before its pointer-down focus transition.
     Detect that already-completed toggle by its changed label without disturbing Tab focus. */
  setTimeout(function () {
    if (!armed) return;
    if (!destination.isConnected) { armed = false; cleanup(); return; }
    if (destination.classList.contains('list-order-toggle')
        && destination.textContent !== initialText) finish();
  }, 0);
}
function _listOrderSet(kind, programmeId, mode) {
  const pref = _listOrderEntryFor(kind, programmeId, true);
  if (!['default', 'nameAsc', 'nameDesc', 'manual'].includes(mode)) mode = 'default';
  if (pref.mode === mode) return;
  if (mode === 'manual' && !pref.ids.length) {
    pref.ids = _listOrderOrdered(_listOrderAll(kind, programmeId), kind, programmeId)
      .map(function (item) { return _listOrderId(kind, item); }).filter(Boolean);
  }
  if (_reorderUiMatches(kind, programmeId) && mode !== 'manual') _reorderUi.editing = false;
  pref.mode = mode;
  const saved = _listOrderPersist();
  if (kind === 'programme') renderPortfolio(); else renderOverview();
  const select = document.querySelector(`.list-order-select[data-order-kind="${CSS.escape(kind)}"][data-programme-id="${CSS.escape(String(programmeId || ''))}"]`);
  if (select) select.focus({ preventScroll: true });
  _listOrderNotifyFailure(saved);
}
function _listOrderSearchActive() { return !!(typeof _hdrQuery === 'string' && _hdrQuery.trim()); }
function _listOrderControlsHtml(kind, programmeId) {
  const pref = _listOrderEntryFor(kind, programmeId, false);
  const pid = _escapeHtml(String(programmeId || ''));
  const opts = [['default','Default'],['nameAsc','Name (A–Z)'],['nameDesc','Name (Z–A)'],['manual','Manual']];
  const editing = _reorderUiEditing(kind, programmeId);
  const noun = kind === 'programme' ? 'programmes' : 'projects';
  const toggle = pref.mode === 'manual'
    ? `<button type="button" class="ov-btn list-order-toggle" data-order-kind="${kind}" data-programme-id="${pid}"`
      + ` aria-label="${editing ? 'Done reordering' : 'Reorder'} ${noun}"`
      + ` aria-disabled="${editing || _listOrderHasMovablePair(kind, programmeId) ? 'false' : 'true'}"`
      + ` onclick="_reorderUiToggle(event,this)">${editing ? 'Done' : 'Reorder'}</button>` : '';
  return `<span class="list-order"><label>Order <select class="list-order-select" data-order-kind="${kind}" data-programme-id="${pid}"`
    + ` onchange="_listOrderSet(this.dataset.orderKind,this.dataset.programmeId,this.value)">`
    + opts.map(function (o) { return `<option value="${o[0]}"${pref.mode === o[0] ? ' selected' : ''}>${o[1]}</option>`; }).join('')
    + `</select></label>${toggle}`
    + (_listOrderSearchActive() && pref.mode === 'manual'
        ? `<span class="list-order-search-hint">Clear the search to reorder the full list.</span>` : '')
    + `</span>`;
}
function _listOrderMoveState(kind, programmeId, item) {
  const ordered = _listOrderOrdered(_listOrderAll(kind, programmeId), kind, programmeId);
  const group = ordered.filter(function (x) { return !!x.archived === !!item.archived; });
  const id = _listOrderId(kind, item);
  const at = group.findIndex(function (x) { return _listOrderId(kind, x) === id; });
  return { up: at > 0, down: at >= 0 && at < group.length - 1 };
}
function _listOrderMoveButtonsHtml(kind, programmeId, item) {
  const state = _listOrderMoveState(kind, programmeId, item);
  const searching = _listOrderSearchActive();
  const id = _escapeHtml(_listOrderId(kind, item));
  const pid = _escapeHtml(String(programmeId || ''));
  const name = _escapeHtml(_listOrderName(kind, item));
  function button(dir, enabled) {
    const allowed = enabled && !searching;
    const label = (dir === 'up' ? 'Move up ' : 'Move down ') + name;
    return `<button type="button" class="list-order-move" data-order-kind="${kind}" data-programme-id="${pid}" data-order-id="${id}" data-order-dir="${dir}"`
      + ` aria-label="${label}" aria-disabled="${allowed ? 'false' : 'true'}" title="${label}"`
      + ` onkeydown="event.stopPropagation()" onclick="_listOrderMove(event,this)">${dir === 'up' ? '↑' : '↓'}</button>`;
  }
  return `<span class="list-order-moves">${button('up', state.up)}${button('down', state.down)}</span>`;
}
function _listOrderMoveHtml(kind, programmeId, item) {
  const pref = _listOrderEntryFor(kind, programmeId, false);
  if (pref.mode !== 'manual') return '';
  const id = _escapeHtml(_listOrderId(kind, item));
  const pid = _escapeHtml(String(programmeId || ''));
  const buttons = _reorderUiEditing(kind, programmeId)
    ? _listOrderMoveButtonsHtml(kind, programmeId, item) : '';
  return `<span class="list-order-move-slot" data-order-kind="${kind}" data-programme-id="${pid}"`
    + ` data-order-id="${id}"${buttons ? '' : ' aria-hidden="true"'}>${buttons}</span>`;
}
function _listOrderSyncUi(kind, programmeId) {
  const root = document.getElementById(kind === 'programme' ? 'portfolioView' : 'overviewView');
  if (!root || !_reorderUiMatches(kind, programmeId)) return;
  const editing = !!_reorderUi.editing;
  const toggle = root.querySelector('.list-order-toggle');
  if (toggle && toggle.dataset.orderKind === kind
      && String(toggle.dataset.programmeId || '') === String(programmeId || '')) {
    const noun = kind === 'programme' ? 'programmes' : 'projects';
    toggle.textContent = editing ? 'Done' : 'Reorder';
    toggle.setAttribute('aria-label', (editing ? 'Done reordering ' : 'Reorder ') + noun);
    toggle.setAttribute('aria-disabled', editing || _listOrderHasMovablePair(kind, programmeId)
      ? 'false' : 'true');
  }
  root.querySelectorAll('.list-order-move-slot').forEach(function (slot) {
    if (slot.dataset.orderKind !== kind
        || String(slot.dataset.programmeId || '') !== String(programmeId || '')) return;
    const item = _listOrderAll(kind, programmeId).find(function (x) {
      return _listOrderId(kind, x) === slot.dataset.orderId;
    });
    slot.innerHTML = editing && item ? _listOrderMoveButtonsHtml(kind, programmeId, item) : '';
    if (slot.innerHTML) slot.removeAttribute('aria-hidden');
    else slot.setAttribute('aria-hidden', 'true');
  });
}
function _reorderUiToggle(event, button) {
  if (event) { event.preventDefault(); event.stopPropagation(); }
  if (!button) return;
  const kind = button.dataset.orderKind;
  const programmeId = button.dataset.programmeId || '';
  if (kind === 'workstream') {
    if (!_wsCanEdit() || !_reorderUiMatches(kind, programmeId)) return;
    if (!_reorderUi.editing && (button.getAttribute('aria-disabled') === 'true'
        || !_wsHasMovablePair())) return;
    _reorderUi.editing = !_reorderUi.editing;
    _wsReorderSyncUi(programmeId);
    const next = document.querySelector(`.wbs-reorder-toggle[data-programme-id="${CSS.escape(programmeId)}"]`);
    if (next) next.focus({ preventScroll:true });
    return;
  }
  const pref = _listOrderEntryFor(kind, programmeId, false);
  if (pref.mode !== 'manual' || !_reorderUiMatches(kind, programmeId)) return;
  if (!_reorderUi.editing && (button.getAttribute('aria-disabled') === 'true'
      || !_listOrderHasMovablePair(kind, programmeId))) return;
  _reorderUi.editing = !_reorderUi.editing;
  _listOrderSyncUi(kind, programmeId);
  const next = (kind === 'programme' ? document.getElementById('portfolioView')
    : document.getElementById('overviewView'))?.querySelector('.list-order-toggle');
  if (next) next.focus();
}
function _listOrderMove(event, button) {
  if (event) { event.preventDefault(); event.stopPropagation(); }
  if (!button || button.getAttribute('aria-disabled') === 'true') return;
  if (button.dataset.orderKind === 'workstream') { _wsOrderMove(event, button); return; }
  if (_listOrderSearchActive()) return;
  const kind = button.dataset.orderKind, programmeId = button.dataset.programmeId || '';
  const id = button.dataset.orderId, dir = button.dataset.orderDir;
  const pref = _listOrderEntryFor(kind, programmeId, true);
  if (pref.mode !== 'manual' || !_reorderUiEditing(kind, programmeId)) return;
  const ordered = _listOrderOrdered(_listOrderAll(kind, programmeId), kind, programmeId);
  const item = ordered.find(function (x) { return _listOrderId(kind, x) === id; });
  if (!item) return;
  const group = ordered.filter(function (x) { return !!x.archived === !!item.archived; });
  const at = group.findIndex(function (x) { return _listOrderId(kind, x) === id; });
  const to = dir === 'up' ? at - 1 : at + 1;
  if (at < 0 || to < 0 || to >= group.length) return;
  const otherId = _listOrderId(kind, group[to]);
  /* Materialise newly seen records at the tail in immutable-key order, then swap the two
     visible neighbours in place. Unknown entries keep their exact slots, so delete/Undo or
     temporary absence cannot quietly rewrite the saved sequence. */
  const sequence = pref.ids.slice();
  ordered.filter(function (x) { return !pref.ids.includes(_listOrderId(kind, x)); })
    .sort(function (a, b) { return _listOrderByKey(a, b, kind); })
    .forEach(function (x) { sequence.push(_listOrderId(kind, x)); });
  const fromIndex = sequence.indexOf(id), toIndex = sequence.indexOf(otherId);
  if (fromIndex < 0 || toIndex < 0) return;
  const swap = sequence[fromIndex]; sequence[fromIndex] = sequence[toIndex]; sequence[toIndex] = swap;
  pref.ids = sequence.filter(function (x, i) { return x && sequence.indexOf(x) === i; });
  const saved = _listOrderPersist();
  if (kind === 'programme') renderPortfolio(); else renderOverview();
  const selector = `.list-order-move[data-order-kind="${CSS.escape(kind)}"][data-programme-id="${CSS.escape(programmeId)}"][data-order-id="${CSS.escape(id)}"][data-order-dir="${CSS.escape(dir)}"]`;
  const next = document.querySelector(selector);
  if (next) {
    const row = next.closest('.pf-row,.pf-project-hit,.pm-row');
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    next.focus();
  }
  _listOrderNotifyFailure(saved);
}
function _dataSrcState() {
  /* v17.4 (W3): this state is about the TAB. It outranks everything because the next permitted
     action is specifically a fresh reload. */
  if (_saveNeedsReload) return { label: 'saved — reload this tab before continuing', warn: true };
  /* ⚠ v13.1 (A10): this line and the file-alert banner are painted on the same screen, so they
     must not be able to disagree: consume the one definition of "blocked". */
  if (_fileAlertBlocked()) return { label: 'not loaded — saving disabled', warn: true };
  if (_srvReach === 'unavailable') return { label: 'server unreachable', warn: true };
  return { label: _srvUser ? 'saving as ' + (_srvUser.displayName || _srvUser.username) : 'not signed in', warn: false };
}
function _dataSrcTitle() {
  return ['Server: ' + location.host,
          'Signed in as: ' + (_srvUser ? (_srvUser.displayName || _srvUser.username) + ' (' + _srvUser.username + ')' : 'nobody'),
          'Save mode: each Save publishes only the records you changed; if a colleague changed the same record first, you are asked which version to keep.'].join('\n');
}
function _dataSrcInnerHtml() {
  const st = _dataSrcState();
  return 'Data: ' + _escapeHtml(location.host) + ' · ' + _escapeHtml(st.label)
    + '<span class="ov-info-i" title="' + _escapeHtml(_dataSrcTitle()) + '">i</span>';
}
function _updateDataSourceLine() {
  /* v10.5.1 (W1): there are TWO landing views, so refresh every data-source line by class. */
  const els = document.querySelectorAll('.ov-datasrc');
  for (let i = 0; i < els.length; i++) {
    els[i].className = 'ov-datasrc' + (_dataSrcState().warn ? ' ov-datasrc-warn' : '');
    els[i].innerHTML = _dataSrcInnerHtml();
  }
  _refreshFileAlerts();   /* v11.1 (W2): one hook, every state change already routes here */
}
